/**
 * WorkspacePill — persistent-chrome workspace identity + switcher
 * (PLAN-multi-org-customer-journey-20260816.md D4 / §10, Phase 3).
 *
 * The org pill renders on every authenticated page: the ACTIVE workspace's
 * name with a deterministic color mark (invariant 3 — two orgs must never
 * look alike at a glance), the user's native org additionally marked
 * "yours". Clicking opens the switcher: one row per workspace, role hint,
 * active check; one click switches.
 *
 * Switching mirrors UserLoginCard's pinned behavior EXACTLY: server-verified
 * `switchActiveWorkspace` → `saveJustJoinedOrg` greeting marker → hard
 * `window.location.assign('/desktop')`. Clean navigation, not SPA state
 * surgery — module-level accountId caches (groupThreads featureFlags, rbac
 * cache, activeWorkspace) only reset on a fresh load.
 *
 * Tolerance contract: any load failure renders NOTHING (the header must
 * never break over workspace listing); `ensureNativeOrg` runs first,
 * best-effort, so an invited-only user's own organization materializes the
 * first time they see the chrome. Labels go through `workspaceLabel` —
 * never a raw account id.
 */
import React, { useEffect, useRef, useState } from 'react';
import styled from 'styled-components';
import { useAuth } from '../context/AuthContext';
import {
  ensureNativeOrg,
  type WorkspaceOption,
} from '../services/rbacService';
import { beginWorkspaceTransition, subscribeWorkspaceTransition } from '../services/workspaceTransition';
import {
  clearWorkspaceOptionsCache,
  fetchWorkspaceOptionsCached,
  patchCachedDisplayNames,
  revalidateWorkspaceOptions,
} from '../services/workspaceOptionsCache';
import { readConfirmedWorkspace } from '../services/prest0nRuntime/confirmedWorkspaceCache';
import { subscribeWorkspaceNames } from '../services/workspaceIndexLive';
import { workspaceMarkColor, workspaceLabel } from '../services/workspaceIdentity';

const PillButton = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 7px;
  max-width: 220px;
  padding: 5px 12px;
  margin-right: 10px;
  border: 1px solid var(--color-border-dark, rgba(127, 127, 127, 0.35));
  border-radius: 999px;
  background: transparent;
  color: inherit;
  font: inherit;
  font-size: 0.82rem;
  cursor: pointer;
  white-space: nowrap;
  &:hover { border-color: var(--color-accent, rgba(127, 127, 127, 0.7)); }
  &:focus-visible { outline: 2px solid currentColor; outline-offset: 2px; }
`;

const Dot = styled.span<{ $color: string }>`
  width: 9px;
  height: 9px;
  flex: 0 0 auto;
  border-radius: 50%;
  background: ${(p) => p.$color};
  /* Definition ring so the mark holds contrast on light chrome. */
  box-shadow: inset 0 0 0 1px rgba(0, 0, 0, 0.15);
`;

const PillName = styled.span`
  overflow: hidden;
  text-overflow: ellipsis;
`;

const YoursTag = styled.span`
  flex: 0 0 auto;
  font-size: 0.68rem;
  opacity: 0.9;
  border: 1px dashed var(--color-accent, rgba(127, 127, 127, 0.6));
  border-radius: 999px;
  padding: 1px 6px;
`;

const Menu = styled.div`
  position: absolute;
  top: calc(100% + 6px);
  right: 0;
  min-width: 260px;
  max-width: 320px;
  border: 1px solid var(--color-border-light, rgba(127, 127, 127, 0.3));
  border-radius: 10px;
  background: var(--color-background-primary, var(--surface, #fff));
  color: inherit;
  box-shadow: 0 8px 28px rgba(0, 0, 0, 0.18);
  padding: 6px;
  z-index: 1200;
`;

const MenuWrap = styled.div`
  position: relative;
  display: inline-flex;
`;

const Row = styled.button<{ $active?: boolean }>`
  display: flex;
  align-items: center;
  gap: 8px;
  width: 100%;
  padding: 8px 10px;
  border: none;
  border-radius: 7px;
  background: transparent;
  color: inherit;
  font: inherit;
  font-size: 0.85rem;
  text-align: left;
  cursor: ${(p) => (p.$active ? 'default' : 'pointer')};
  opacity: ${(p) => (p.$active ? 1 : 0.92)};
  &:hover { background: var(--color-background-hover, rgba(127, 127, 127, 0.12)); }
  &:focus-visible { outline: 2px solid currentColor; outline-offset: -2px; }
`;

const RowName = styled.span`
  flex: 1 1 auto;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const RowHint = styled.span`
  flex: 0 0 auto;
  font-size: 0.7rem;
  opacity: 0.65;
`;

const WorkspacePill: React.FC = () => {
  const { isAuthenticated, user } = useAuth();
  const [workspaces, setWorkspaces] = useState<WorkspaceOption[]>([]);
  const [liveNames, setLiveNames] = useState<Record<string, string>>({});
  const [open, setOpen] = useState(false);
  const [switchingId, setSwitchingId] = useState<string | null>(null);
  // Declared with the other hooks, ABOVE this component's early return: the
  // first version of this sat next to the handler it belongs to, three lines
  // below `if (!isAuthenticated || workspaces.length === 0) return null`, so
  // a render that took the early return called one fewer hook than the next
  // one and React threw "Rendered more hooks than during the previous
  // render". The switcher's own suite caught it.
  const [settingAside, setSettingAside] = useState(false);
  const wrapRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (!isAuthenticated) return;
    let alive = true;
    const persistUid = user?.id || null;
    // Speed round (owner report 2026-08-16): the list no longer waits behind
    // ensureNativeOrg — the persisted server-verified copy paints instantly,
    // the callable revalidates in the background, and the ensure runs in
    // PARALLEL with a forced refresh afterwards so a first-ever sign-in still
    // converges on the freshly minted native org within one extra fetch.
    fetchWorkspaceOptionsCached(persistUid)
      .then((options) => {
        if (alive && options.length > 0) setWorkspaces(options);
      })
      .catch(() => {
        if (alive) setWorkspaces([]);
      });
    ensureNativeOrg()
      .then(() => revalidateWorkspaceOptions(persistUid))
      .then((options) => {
        if (alive && options.length > 0) setWorkspaces(options);
      })
      .catch(() => undefined);
    return () => {
      alive = false;
    };
  }, [isAuthenticated, user?.id]);

  // Live rename channel (owner directive 2026-08-17): org renames land on
  // users/{uid}/workspace_index server-side and must repaint this chrome in
  // real time, for every member, without a reload. Names from the index
  // OVERRIDE list labels (labels only — membership/switching stay with the
  // verified callables), and the shared cache is patched so the next paint
  // anywhere starts from the renamed labels too.
  useEffect(() => {
    if (!isAuthenticated || !user?.id) return;
    const uid = user.id;
    return subscribeWorkspaceNames(uid, (names) => {
      setLiveNames(names);
      patchCachedDisplayNames(uid, names);
    });
  }, [isAuthenticated, user?.id]);

  // A failed sealed switch releases the overlay — re-enable the rows so the
  // user can try again (success never comes back: the page reloads).
  useEffect(() => subscribeWorkspaceTransition((s) => {
    if (!s) setSwitchingId(null);
  }), []);

  // Close on outside click / Escape.
  useEffect(() => {
    if (!open) return;
    const onDown = (e: MouseEvent) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target as Node)) setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false);
    };
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDown);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  if (!isAuthenticated || workspaces.length === 0) return null;

  const displayed = workspaces.map((w) =>
    liveNames[w.accountId] ? { ...w, displayName: liveNames[w.accountId] } : w,
  );
  const uid = user?.id;
  // ORG STICKINESS (INCIDENT-20260818): the pill must never GUESS the
  // current org. The old first-row fallback painted the alphabetically-first
  // workspace whenever the cached list carried no isActive row (stale 7-day
  // snapshot / verification dropping the active account) — the user read it
  // as a spontaneous org switch. Preference: the LAST CONFIRMED workspace
  // (written only on successful strict resolution) → the list's isActive →
  // a neutral resolving state. Never a guess. (PR #925 reconciliation:
  // main's confirmed-workspace preference supersedes this branch's parallel
  // getLastWorkspace fallback.)
  const confirmed = readConfirmedWorkspace(uid);
  const active =
    (confirmed ? displayed.find((w) => w.accountId === confirmed.accountId) : undefined)
    ?? displayed.find((w) => w.isActive)
    ?? null;
  // Full roster feeds collision avoidance so every visible dot stays distinct.
  const rosterIds = workspaces.map((w) => w.accountId);

  // "this isn't what i wanted" (owner ask 2026-09-09). Choosing the personal
  // workspace used to be one-way: leaveOrganization refuses the native org
  // ("there is nothing to leave"), and an account whose only workspace is
  // this one has nothing to switch to. Setting it aside returns it to the
  // "set up" row it came from — deleting nothing, so picking it again
  // restores everything.
  //
  // The server decides whether the control appears at all (canSetAside): it
  // needs the lazy flag AND the posture, and an affordance that might be
  // refused is worse than none.
  const setAsideRow = displayed.find((w) => w.canSetAside);
  const handleSetAside = async (ws: WorkspaceOption) => {
    if (settingAside || switchingId) return;
    setSettingAside(true);
    try {
      const { deactivatePersonalWorkspace } = await import('../services/rbacService');
      const res = await deactivatePersonalWorkspace();
      if (!res.deactivated && res.reason === 'eager-mode') {
        // The fleet is not in lazy mode: the platform would re-activate this
        // workspace on the next page mount. Say so rather than pretending.
        setSettingAside(false);
        setOpen(false);
        return;
      }
      try { clearWorkspaceOptionsCache(); } catch { /* revalidates on load */ }
      // Hard navigation, the same reload-through contract every workspace
      // change obeys: module-level accountId caches re-key only on a fresh
      // load. /onboarding is where the personal-vs-company choice lives.
      window.location.assign('/onboarding');
    } catch {
      setSettingAside(false);
    }
  };

  const handleSwitch = (ws: WorkspaceOption) => {
    if (ws.isActive || switchingId) return;
    setSwitchingId(ws.accountId);
    // SEALED SWITCH (2026-08-19; floor revised 2026-08-20): the transition
    // controller raises the full-screen blocking overlay (MIN_VISIBLE_MS
    // floor overlapped with the work), runs the server-verified switch
    // beneath it, writes every arrival marker (greeting, cache active flag,
    // last-workspace memory, boot continuation), then hard-RELOADS in place
    // (owner directive 2026-08-16: switching keeps the user on the page
    // they are on; the reloaded page re-keys to the new workspace).
    // DORMANT rows ride the same sealed path — the controller detects them
    // from the roster and activates + lands on /onboarding (2026-08-20:
    // this used to bypass the overlay entirely).
    beginWorkspaceTransition({
      targetAccountId: ws.accountId,
      targetLabel: workspaceLabel(ws, uid),
      uid,
      navigation: 'reload',
    });
  };

  return (
    <MenuWrap ref={wrapRef}>
      <PillButton
        type="button"
        aria-haspopup="menu"
        aria-expanded={open}
        aria-label={active
          ? `Workspace: ${workspaceLabel(active, uid)}. Open switcher.`
          : 'Workspace resolving. Open switcher.'}
        onClick={() => setOpen((v) => !v)}
        data-testid="workspace-pill"
      >
        {active ? (
          <>
            <Dot $color={workspaceMarkColor(active.accountId, rosterIds)} aria-hidden="true" />
            <PillName>{workspaceLabel(active, uid)}</PillName>
            {active.isOwner && <YoursTag>yours</YoursTag>}
          </>
        ) : (
          /* Neutral resolving state — honest over a guessed org. */
          <PillName>resolving workspace…</PillName>
        )}
      </PillButton>
      {open && (
        <Menu role="menu" aria-label="Switch workspace">
          {/* No "organization details…" row (owner directive 2026-08-16 #2:
              redundant — the /organization page opens on the ACTIVE workspace
              and is reachable from the profile dropdown). The pill switches
              workspaces — plus the one creation affordance the 2026-08-18
              owner directive added: "start a new organization" (task #75). */}
          {displayed.map((ws) => {
            const label = workspaceLabel(ws, uid);
            return (
              <Row
                key={ws.accountId}
                type="button"
                role="menuitem"
                $active={ws.isActive}
                aria-current={ws.isActive ? 'true' : undefined}
                disabled={Boolean(switchingId)}
                onClick={() => handleSwitch(ws)}
                data-testid={`workspace-row-${ws.accountId}`}
              >
                <Dot $color={workspaceMarkColor(ws.accountId, rosterIds)} aria-hidden="true" />
                <RowName>{label}</RowName>
                {/* "yours" = VERIFIED ownership — founded it or was made the
                    owner later (owner directive 2026-08-16: a user can own
                    multiple workspaces and every one of them says so). */}
                {ws.isOwner && <YoursTag>yours</YoursTag>}
                <RowHint>
                  {switchingId === ws.accountId
                    ? (ws.dormant ? 'setting up…' : 'switching…')
                    : ws.isActive
                      ? '✓ current'
                      : ws.dormant
                        ? 'set up'
                        : ws.isOwner
                          ? ''
                          : ws.roleLabel || 'member'}
                </RowHint>
              </Row>
            );
          })}
          {setAsideRow && (
            <Row
              type="button"
              role="menuitem"
              disabled={settingAside || Boolean(switchingId)}
              onClick={() => { void handleSetAside(setAsideRow); }}
              data-testid="workspace-set-aside"
            >
              <RowName>
                {settingAside ? 'setting aside…' : `set aside ${workspaceLabel(setAsideRow, uid)}`}
              </RowName>
              <RowHint>nothing is deleted</RowHint>
            </Row>
          )}
          {/* Task #75 (owner directive 2026-08-18): start ANOTHER
              organization — the exact same staged onboarding runs for it,
              and the org only exists once that setup completes (the server
              re-verifies the creator finished their own onboarding first). */}
          <Row
            type="button"
            role="menuitem"
            onClick={() => {
              setOpen(false);
              window.location.assign('/onboarding?neworg=1');
            }}
            data-testid="workspace-create-org"
            style={{ borderTop: '1px solid var(--color-border-light, rgba(127,127,127,0.25))', borderRadius: 0, marginTop: '4px', paddingTop: '10px' }}
          >
            <RowName>+ start a new organization</RowName>
          </Row>
        </Menu>
      )}
    </MenuWrap>
  );
};

export default WorkspacePill;
